import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

// App-wide "which program am I viewing" state. Reports (hitting / pitching /
// defense) scope to selectedTeam.code — the TM_Worldwide short code, e.g.
// 'TEX_AGG'. Seeded from the logged-in account's bound team, else a remembered
// pick in localStorage, else null (which triggers the post-login picker modal).
const TeamContext = createContext();

export function useTeam() {
  return useContext(TeamContext);
}

const STORAGE_KEY = 'dugoutscout_selected_team';

// Client-side mirror of the backend's _prettify_team_code(): a readable fallback
// name when we only know the code (e.g. seeded from the account's bound team).
function prettify(code) {
  if (!code) return '';
  return String(code).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function readStored() {
  try {
    const s = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (s && s.code) return { code: s.code, name: s.name || prettify(s.code) };
  } catch {
    /* ignore malformed value */
  }
  return null;
}

export function TeamProvider({ children }) {
  const { currentUser } = useAuth();

  const [selectedTeam, setSelectedTeamState] = useState(() => {
    const stored = readStored();
    if (stored) return stored;
    const t = currentUser?.team;
    return t ? { code: t, name: prettify(t) } : null;
  });

  // Re-seed on login / clear on logout as currentUser changes.
  useEffect(() => {
    if (!currentUser) {
      setSelectedTeamState(null);
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      return;
    }
    // A login bound to a team always wins over a stale local pick.
    if (currentUser.team) {
      setSelectedTeamState((prev) =>
        prev && prev.code === currentUser.team
          ? prev
          : { code: currentUser.team, name: prettify(currentUser.team) }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Resolve the full program display name from its code. When a team is auto-loaded
  // from the account (not picked in the modal), we only have a prettified code like
  // "TEX LON"; ask the backend for the real name ("Texas Longhorns") and canonicalize.
  useEffect(() => {
    const code = selectedTeam?.code;
    if (!code) return;
    let cancelled = false;
    fetch(`/api/team/name?team=${encodeURIComponent(code)}`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled || !d || !d.name || d.name === selectedTeam.name) return;
        const value = { code, name: d.name };
        setSelectedTeamState(value);
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(value)); } catch { /* ignore */ }
      })
      .catch(() => { /* non-blocking */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeam?.code]);

  const setSelectedTeam = (team) => {
    if (!team || !team.code) return;
    const value = { code: team.code, name: team.name || prettify(team.code) };
    setSelectedTeamState(value);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(value)); } catch { /* ignore */ }

    // Best-effort: bind the pick to the account so this login stays on its team.
    if (currentUser?.id) {
      fetch(`/api/users/${currentUser.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ team: value.code }),
      }).catch(() => { /* non-blocking */ });
    }
  };

  const value = {
    selectedTeam,
    setSelectedTeam,
    teamCode: selectedTeam?.code || null,
  };

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
}
